/**
 * The Website Analytics series and headline — PURE. Everything a number on that
 * page means is decided here, so the page renders what it is given.
 *
 * ⚠️⚠️ THREE RULES, each from src/lib/portalReport/series.ts and each tested:
 *
 *   1. AN UNPULLED DAY IS NULL, NOT ZERO. GA4 returns no row for a day with no
 *      traffic, so "no row" is ambiguous; `ga4_pull_coverage` is what says a day
 *      was pulled. A covered day with no rows is a MEASURED zero; an uncovered
 *      one is unmeasured and its bucket goes `no-data` (a gap, never a bar at 0).
 *   2. EVERY RATE IS RE-DERIVED PER BUCKET FROM THAT BUCKET'S SUMS. Bounce rate =
 *      1 − Σengaged ÷ Σsessions; average duration = Σduration ÷ Σsessions. Never
 *      an average of daily rates, never stored — the deriveCostPerLead rule.
 *   3. ⚠️⚠️ A STUDIO BEFORE IT OPENED IS A GAP, NOT A FAILURE. GA4 correctly
 *      reports zero traffic to a page that did not exist yet, and coverage
 *      correctly calls that day measured — so without `liveFrom` a studio that
 *      opened in March draws a flat line at zero for the year before, which
 *      reads as a studio that did nothing. Days before `liveFrom` are treated as
 *      unmeasured FOR DISPLAY. The rows still land; only the reading changes.
 *      Only the client knows the opening date — it is config, never inferred.
 */

import type { PortalBucketCoverage, PortalGrouping } from '../../types/portalReport.js';
import type { PortalWebsiteHeadline, PortalWebsitePoint } from '../../types/portalWebsite.js';
import { dayLabel, enumerateDays, monthKeyOf, monthLabel } from '../portalReport/series.js';
import { deriveBounceRate } from './metrics.js';

/** One day's sums for the target (site or one studio), as read from ga4_daily. */
export interface WebsiteDayRow {
  date: string; // YYYY-MM-DD
  sessions: number;
  engagedSessions: number;
  sessionDurationSec: number;
  /** Null when the target does not carry new users on these rows. */
  newUsers: number | null;
}

export interface WebsiteMeasurement {
  /** Days a pull covered (from ga4_pull_coverage). */
  coveredDates: ReadonlySet<string>;
  /** The studio's opening date, or null (the site, or not known). */
  liveFrom: string | null;
  /** The client's today — the bucket containing it is in progress. */
  today: string;
}

/** Was this day measured, for display? Pulled AND after the studio opened. */
export function isMeasuredDay(date: string, m: WebsiteMeasurement): boolean {
  if (m.liveFrom !== null && date < m.liveFrom) return false;
  return m.coveredDates.has(date);
}

export function deriveAvgDuration(durationSec: number | null, sessions: number | null): number | null {
  if (durationSec === null || sessions === null || sessions <= 0) return null;
  return durationSec / sessions;
}

interface Sums {
  sessions: number;
  engagedSessions: number;
  sessionDurationSec: number;
  newUsers: number | null;
  measuredDays: number;
}

function sumOver(dates: readonly string[], byDate: ReadonlyMap<string, WebsiteDayRow>, m: WebsiteMeasurement): Sums {
  const s: Sums = { sessions: 0, engagedSessions: 0, sessionDurationSec: 0, newUsers: null, measuredDays: 0 };
  for (const d of dates) {
    if (!isMeasuredDay(d, m)) continue;
    s.measuredDays += 1;
    const r = byDate.get(d);
    if (!r) continue; // a measured day with no traffic — a real zero
    s.sessions += r.sessions;
    s.engagedSessions += r.engagedSessions;
    s.sessionDurationSec += r.sessionDurationSec;
    if (r.newUsers !== null) s.newUsers = (s.newUsers ?? 0) + r.newUsers;
  }
  return s;
}

/**
 * One bucket's coverage, from how many of its days were measured.
 * ⚠️ Order matters: nothing measured → `no-data` BEFORE the in-progress check, so
 * an unmeasured current month is a gap rather than an "in progress" zero.
 */
export function websiteBucketCoverage(days: readonly string[], m: WebsiteMeasurement): PortalBucketCoverage {
  const elapsed = days.filter((d) => d <= m.today);
  const measured = elapsed.filter((d) => isMeasuredDay(d, m)).length;
  if (measured === 0) return 'no-data';
  if (days[days.length - 1]! >= m.today) return 'in-progress';
  return measured < days.length ? 'partial' : 'complete';
}

/**
 * The series for [from, to] at `grouping`. Buckets are clipped to the window, so
 * a monthly bucket at either edge sums only the days inside it.
 */
export function buildWebsiteSeries(opts: {
  rows: readonly WebsiteDayRow[];
  from: string;
  to: string;
  grouping: PortalGrouping;
  measurement: WebsiteMeasurement;
  /** When false (a studio series), newUsers is always null on the points. */
  carriesNewUsers: boolean;
}): PortalWebsitePoint[] {
  const byDate = new Map(opts.rows.map((r) => [r.date, r]));
  const days = enumerateDays(opts.from, opts.to);
  const buckets = new Map<string, string[]>();
  for (const d of days) {
    const key = opts.grouping === 'monthly' ? monthKeyOf(d) : d;
    const list = buckets.get(key) ?? [];
    list.push(d);
    buckets.set(key, list);
  }
  return [...buckets.entries()].map(([bucket, bucketDays]) => {
    const coverage = websiteBucketCoverage(bucketDays, opts.measurement);
    const label = opts.grouping === 'monthly' ? monthLabel(bucket) : dayLabel(bucket);
    if (coverage === 'no-data') {
      return { bucket, label, coverage, sessions: null, engagedSessions: null, sessionDurationSec: null, newUsers: null };
    }
    const s = sumOver(bucketDays, byDate, opts.measurement);
    return {
      bucket,
      label,
      coverage,
      sessions: s.sessions,
      engagedSessions: s.engagedSessions,
      sessionDurationSec: s.sessionDurationSec,
      newUsers: opts.carriesNewUsers ? (s.newUsers ?? 0) : null,
    };
  });
}

/**
 * The four headline figures over [from, to]. ⚠️ Null (never 0) when not a single
 * day of the period was measured — the page then says so rather than printing
 * four zeros.
 */
export function buildWebsiteHeadline(opts: {
  sessionRows: readonly WebsiteDayRow[];
  /** Where new users come from (the same rows for the site; landing rows for a studio). */
  newUserRows: readonly WebsiteDayRow[];
  newUsersBasis: PortalWebsiteHeadline['newUsersBasis'];
  from: string;
  to: string;
  measurement: WebsiteMeasurement;
}): PortalWebsiteHeadline & { measuredDays: number; days: number } {
  const days = enumerateDays(opts.from, opts.to).filter((d) => d <= opts.measurement.today);
  const s = sumOver(days, new Map(opts.sessionRows.map((r) => [r.date, r])), opts.measurement);
  const nu = sumOver(days, new Map(opts.newUserRows.map((r) => [r.date, r])), opts.measurement);
  const none = s.measuredDays === 0;
  return {
    sessions: none ? null : s.sessions,
    newUsers: none ? null : (nu.newUsers ?? 0),
    newUsersBasis: opts.newUsersBasis,
    bounceRate: none ? null : deriveBounceRate(s.sessions, s.engagedSessions),
    avgSessionDurationSec: none ? null : deriveAvgDuration(s.sessionDurationSec, s.sessions),
    measuredDays: s.measuredDays,
    days: days.length,
  };
}

// ---- chart metrics ----------------------------------------------------------

/**
 * The four website ChartMetrics. ⚠️ Read by `websiteValueAt`, never by the ad
 * `valueAt` — a website bucket is a different SHAPE, the CRM precedent. ⚠️ No
 * engaged-sessions chip: it is bounce rate inverted, and the two on one chart
 * read as two findings.
 */
export type WebsiteMetricKey = 'webSessions' | 'webNewUsers' | 'webBounceRate' | 'webAvgDuration';

export const WEBSITE_METRIC_KEYS = ['webSessions', 'webNewUsers', 'webBounceRate', 'webAvgDuration'] as const satisfies readonly WebsiteMetricKey[];

export function isWebsiteMetric(metric: string): metric is WebsiteMetricKey {
  return (WEBSITE_METRIC_KEYS as readonly string[]).includes(metric);
}

/** A bucket's value for a metric, re-derived from its sums. Bounce as a PERCENTAGE. */
export function websiteValueAt(p: PortalWebsitePoint, metric: WebsiteMetricKey): number | null {
  if (metric === 'webSessions') return p.sessions;
  if (metric === 'webNewUsers') return p.newUsers;
  if (metric === 'webBounceRate') {
    const b = deriveBounceRate(p.sessions, p.engagedSessions);
    return b === null ? null : b * 100;
  }
  return deriveAvgDuration(p.sessionDurationSec, p.sessions);
}
